import * as Sentry from '@sentry/react';

export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';

  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn('Sentry DSN not found. Error tracking disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Send default PII (IP address, etc.)
    sendDefaultPii: true,

    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in production, 100% in dev

    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Release tracking
    release: process.env.REACT_APP_VERSION || 'dev',

    // Filter errors
    beforeSend(event, hint) {
      // Don't send errors in development (optional)
      if (environment === 'development') {
        console.error('Sentry Event (dev):', event, hint);
        return null; // Prevents sending to Sentry in dev
      }

      // Filter out specific errors you want to ignore
      const error = hint.originalException;
      
      // Ignore network errors that are user's connection issues
      if (error instanceof Error) {
        if (error.message.includes('Network Error') || 
            error.message.includes('Failed to fetch')) {
          return null;
        }
      }

      return event;
    },

    // Add user context when available
    initialScope: {
      tags: {
        app: 'admin-dashboard',
      },
    },
  });
};

// Helper to set user context
export const setUserContext = (admin: { id: string; email?: string; name?: string }) => {
  Sentry.setUser({
    id: admin.id,
    email: admin.email,
    username: admin.name,
  });
};

// Helper to clear user context (on logout)
export const clearUserContext = () => {
  Sentry.setUser(null);
};

// Helper to add breadcrumb
export const addBreadcrumb = (message: string, category: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
};

// Helper to capture exception manually
export const captureException = (error: Error, context?: Record<string, any>) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

// Helper to capture message manually
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  Sentry.captureMessage(message, level);
};
